import type { ContainerOrderLine } from "../types/containerOrders";
import { activeStopsOf, completedStepCount } from "./containerLineSteps";

/**
 * The driver's day, read off the one order the server put it in.
 *
 * Two questions are answered from this file and they are the same question:
 * "what am I doing next" (the card under the route timeline) and "may I open
 * this" (the lock on every other card). Both are positional, so both are
 * answered from one sequence — the active list exactly as `getDriverLines`
 * returned it.
 *
 * ── Where the order comes from, and why not from here ─────────────────────
 *
 * The server sorts by the planning board's day and slot, then the pickup
 * terminal's date, then the container's sequence, then its id. Board first
 * because a placed container carries a dispatcher's own statement of the order
 * and nothing computed should outrank it; the terminal date underneath so a
 * company that never opens the board is ordered exactly as it always was; the
 * last two because they can never be absent, which is what makes the result
 * total and stable rather than whatever the database felt like returning.
 *
 * This file used to re-sort that list with four rules of its own, written
 * before the board existed. They no longer improved on the server's answer —
 * they contradicted it, most visibly by promoting a container already under way
 * ahead of one not begun. Harmless while only a "what's next" card read it;
 * not harmless once the same sequence decides which containers a driver may
 * open, because the lock reads the list's order and the card read a different
 * one. See `driverQueue`.
 *
 * So: the server orders, this filters, and the two screens cannot disagree.
 * Callers must pass the active list as the API returned it.
 */

/**
 * When this container is next due somewhere, or null if nobody has said.
 *
 * The first stop it has *not* finished and *is* going to make — a stop already
 * behind the driver is not what the container is waiting for, and one marked as
 * not used is not a place anybody is going. The cut-off stands in when no stop
 * carries a date: it is the deadline the whole run answers to, and a container
 * with only a cut-off is still a container with a moment attached.
 *
 * A stop with a date but no time is read as the start of that day rather than
 * discarded. Losing a whole day's worth of ordering over a missing `HH:mm` would
 * be letting the more precise field veto the less precise one.
 */
export function nextDueAt(line: ContainerOrderLine): number | null {
  const stops = activeStopsOf(line);
  const done = completedStepCount(line);
  // Two steps per stop, so the stop being worked is the one the step count
  // falls inside. Anything before it is finished.
  const fromIndex = Math.floor(done / 2);

  for (const stop of stops.slice(fromIndex)) {
    if (!stop.date) continue;
    const at = new Date(stop.date);
    if (Number.isNaN(at.getTime())) continue;
    applyClock(at, stop.time);
    return at.getTime();
  }

  if (line.cutOffDate) {
    const at = new Date(line.cutOffDate);
    if (!Number.isNaN(at.getTime())) {
      applyClock(at, line.cutOffTime);
      return at.getTime();
    }
  }

  return null;
}

/** `HH:mm` onto a date, where one was given. Midnight is the honest default. */
function applyClock(date: Date, time?: string | null): void {
  const [hours, minutes] = String(time ?? "")
    .split(":")
    .map(Number);
  date.setHours(
    Number.isFinite(hours) ? hours : 0,
    Number.isFinite(minutes) ? minutes : 0,
    0,
    0,
  );
}

/**
 * The driver's remaining containers, in the order they are going to be driven.
 *
 * `current` drops out — it is the one on screen — and so does anything already
 * finished or called off, which the API's `active` scope has already excluded;
 * the filter here is the backstop for a caller that fetched some other way.
 *
 * Crosses orders on purpose. A driver's day is a queue of boxes, not a booking:
 * the next container is as likely to belong to a different order as to this
 * one, and grouping by order would answer a question nobody at the wheel is
 * asking.
 *
 * ── Why this no longer sorts ───────────────────────────────────────────────
 *
 * It used to, with four tie-breaking rules of its own, and the rules were
 * written before the planning board existed — back when the server could only
 * order the list by a terminal's date and the client had to do better. The
 * server orders it properly now (`getDriverLines`: the board's day and slot,
 * then the terminal date, then the sequence, then the id), so a second sort
 * here is not a refinement of that answer. It is a *different* answer, and the
 * two disagreed: this one promoted a container already under way ahead of one
 * not begun, which the server's does not.
 *
 * That disagreement had no visible consequence while the only thing reading it
 * was a "what's next" card. It acquired one the moment the queue started
 * deciding which containers a driver may open — because the lock names the
 * first box in the *list's* order, and this named a possibly different box as
 * the next one. A driver would be told to finish one container by the banner
 * and offered another by the card beneath it.
 *
 * So there is one order and the server owns it. The input must be the active
 * list as the API returned it; this filters and does not reorder. That is also
 * the honest arrangement under paging — a client holding one page cannot sort
 * a list it has not got.
 *
 * @see lockReason — the other reader of that same order
 */
export function driverQueue(
  lines: ContainerOrderLine[],
  currentId?: string,
): ContainerOrderLine[] {
  return lines.filter(
    (line) =>
      line._id !== currentId &&
      line.status !== "completed" &&
      line.status !== "cancelled",
  );
}

/** The one container the driver goes to next, or null when the day is done. */
export const nextContainerFor = (
  lines: ContainerOrderLine[],
  currentId?: string,
): ContainerOrderLine | null => driverQueue(lines, currentId)[0] ?? null;

/**
 * Why a container is not the driver's to work yet, or null when it is.
 *
 * A truck pulls one box at a time, and the cab's list was offering all of them
 * at once. Tapping the fourth card opened a fully working execution screen —
 * every stop confirmable, every document fileable — so the order dispatch
 * planned was a suggestion the screen did nothing to hold. Working out of turn
 * is not a hypothetical misuse either: the cards look alike, the list is long,
 * and the one a thumb lands on while a truck is moving is whichever was under
 * it.
 *
 * So exactly one container on the active list is live: the first one that is
 * not finished, in the order the server already sorted — the planning board's
 * own answer, with the terminal's date underneath it (see `getDriverLines`).
 * The list is drawn in that order, which is what makes "the first one" a rule a
 * driver can see rather than one they have to be told.
 *
 * ── Why progress does not unlock anything ──────────────────────────────────
 *
 * It did, briefly, and that was the whole of the bug: a box already under way
 * was treated as live wherever it sat in the list, on the reasoning that a
 * container demoted mid-run must not strand its driver. But a truck's active
 * list is *mostly* boxes under way — a four-container day has four of them
 * part-finished by mid-afternoon — so the exemption applied to nearly every
 * card and the lock applied to nearly none. The screenshot that found it had
 * two containers, both in progress, both open.
 *
 * The rule is positional and nothing else. First unfinished is live; everything
 * behind it is not, whatever its status says, because "what am I doing next" is
 * a question about order and not about progress. The stranding worry it was
 * meant to answer is answered properly instead: the banner on a locked
 * container names the box in front and offers a button straight to it, so a
 * driver whose day was reordered under them is one tap from the right screen
 * rather than being quietly handed the wrong one.
 *
 * A finished or cancelled container is never locked. Neither is work owing, and
 * the completed tab is a record a driver looks things up in; a lock there would
 * be refusing to show somebody their own afternoon.
 *
 * Deliberately a *reason* rather than a boolean. The card has to say which
 * container is in the way — "finish FC-1043/2 first" is actionable where a grey
 * card with a padlock is a bug report — and the execution screen has to say the
 * same thing in a banner, from the same rule.
 */
export interface QueueLock {
  /** The container that has to be finished first. */
  blockedBy: ContainerOrderLine;
}

export function lockReason(
  line: ContainerOrderLine,
  /**
   * The active list in the order it is shown, which is the order the server
   * sorted. Passing the rendered list rather than re-sorting here is the point:
   * a lock computed from a different order than the one on screen would point
   * at a card that is not the one at the top.
   */
  ordered: ContainerOrderLine[],
): QueueLock | null {
  if (isSettled(line)) return null;

  /*
   * The container at the head of the queue — skipping anything that has
   * dropped out of it. A completed box sitting in a stale list must not be the
   * thing everything behind it is waiting for: the list is refetched on a plan
   * change, but between the step that finished a run and the refetch that
   * notices, the whole rest of the day would read as blocked by a container
   * already done.
   */
  const head = ordered.find((candidate) => !isSettled(candidate));

  // No head at all, or this *is* the head. Either way there is nothing in front
  // of it. A line absent from `ordered` is also unlocked — a caller holding one
  // page of a list cannot honestly claim a container it cannot see is blocked.
  if (!head || head._id === line._id) return null;
  if (!ordered.some((candidate) => candidate._id === line._id)) return null;

  return { blockedBy: head };
}

/** Off the to-do list for good: done, or called off. */
const isSettled = (line: ContainerOrderLine): boolean =>
  line.status === "completed" || line.status === "cancelled";
